import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronLeft, CalendarDays, Clock, MapPin, Lock, Star, Check, Link as LinkIcon, Download, GraduationCap,
} from 'lucide-react';
import * as api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import EventCheckout from './EventCheckout';
import {
  kindOf, formatPrice, formatEventDate, formatEventTime, formatDateTimeShort, refundDeadline, downloadIcs, googleCalendarUrl,
} from '../lib/eventsFormat';

// **double asterisks** make bold text. Everything is rendered as text, so nothing typed here can inject HTML.
const BoldText = ({ text }) => text.split(/\*\*(.+?)\*\*/gs).map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));

const Hero = ({ event }) => {
  const kind = kindOf(event);
  return (
    <div className="relative h-[190px] sm:h-[250px] rounded-arch-xl overflow-hidden bg-gradient-to-br from-teal to-lavender">
      {event.image_url ? (
        <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" style={{ objectPosition: `50% ${event.image_focus_y ?? 50}%` }} />
      ) : (
        <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 600 250" preserveAspectRatio="none" aria-hidden="true">
          <g fill="none" stroke="#fff" strokeWidth="2">
            <path d="M20 250V140a50 50 0 0 1 100 0v110M150 250V120a60 60 0 0 1 120 0v130M300 250V140a50 50 0 0 1 100 0v110M430 250V110a65 65 0 0 1 130 0v140" />
          </g>
        </svg>
      )}
      <span className="absolute left-5 top-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/90 text-teal-dark text-xs font-bold">
        <kind.Icon className="w-3.5 h-3.5" aria-hidden="true" />{kind.label}
      </span>
    </div>
  );
};

const MetaRow = ({ Icon, label, value }) => (
  <div className="flex gap-3.5 items-center">
    <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary to-secondary text-white flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5" aria-hidden="true" />
    </span>
    <div>
      <p className="text-xs text-text-muted">{label}</p>
      <p className="font-heading font-semibold text-base text-text-main">{value}</p>
    </div>
  </div>
);

const SpotsBar = ({ event }) => {
  if (!event.capacity || event.spots_left === null || event.spots_left === undefined) return null;
  // With separate brother and sister seats the counts stay private: only "Sold out" and "Almost sold out" are shown
  if (event.separate_by_gender) {
    const note = event.spots_left === 0 ? 'Sold out' : event.spots_left <= 3 ? 'Almost sold out' : '';
    return note ? <p className="mt-4 text-[12.5px] text-text-muted" data-testid="event-spots">{note}</p> : null;
  }
  const taken = Math.max(0, event.capacity - event.spots_left);
  const pct = Math.min(100, Math.round((taken / event.capacity) * 100));
  return (
    <div className="mt-4" data-testid="event-spots">
      <div className="h-2.5 rounded-full bg-teal/15 overflow-hidden" role="img" aria-label={`${taken} of ${event.capacity} spots taken`}>
        <div className="h-full progress-bar rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex justify-between text-[12.5px] text-text-muted mt-1.5">
        <span>{event.spots_left > 0 ? `${event.spots_left} spot${event.spots_left === 1 ? '' : 's'} left` : 'Sold out'}</span>
        <span>{event.capacity} spots</span>
      </div>
    </div>
  );
};

/** Shown when an event is sold out. There is no waitlist: people ask to hear about the next event instead. */
const NotifyMe = ({ member }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | busy | on_list | sent
  const [error, setError] = useState('');

  const submit = async (e) => {
    if (e) e.preventDefault();
    setStatus('busy');
    setError('');
    try {
      const res = member ? await api.eventNotifyMember() : await api.eventNotifyPublic(email.trim());
      setStatus(res.data?.status === 'sent' ? 'sent' : 'on_list');
    } catch (err) {
      setStatus('idle');
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again in a moment.');
    }
  };

  return (
    <div className="mt-4 p-4 rounded-arch bg-lavender/15 text-sm leading-relaxed text-text-main" data-testid="event-notify">
      <p className="font-semibold">Want to hear about the next one? 💌</p>
      <p className="text-[13px] text-text-muted mt-1">Sirati members hear about new events before we announce them publicly.</p>
      <div role="status" aria-live="polite">
        {status === 'on_list' && <p className="mt-3 font-semibold text-teal-dark dark:text-teal-light">You are on the list. We will let you know.</p>}
        {status === 'sent' && <p className="mt-3 font-semibold text-teal-dark dark:text-teal-light">Almost done. Check your inbox and tap the confirmation link.</p>}
      </div>
      {status !== 'on_list' && status !== 'sent' && (
        <form onSubmit={submit} className="mt-3" noValidate>
          {!member && (
            <>
              <label htmlFor="event-notify-email" className="block text-[13px] font-semibold mb-1">Your email</label>
              <input id="event-notify-email" type="email" autoComplete="email" inputMode="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} aria-invalid={!!error} aria-describedby={error ? 'event-notify-error' : undefined}
                className="w-full px-4 py-3 bg-white/80 dark:bg-white dark:text-gray-800 rounded-arch border border-primary/25 focus:ring-2 focus:ring-primary focus:outline-none min-h-[44px]" />
            </>
          )}
          {error && <p id="event-notify-error" role="alert" className="mt-2 text-[13px] text-red-600">{error}</p>}
          <button type="submit" disabled={status === 'busy' || (!member && !email.trim())}
            className="mt-3 min-h-[44px] w-full rounded-full bg-primary text-white font-bold btn-hover disabled:opacity-60">
            {status === 'busy' ? 'One moment...' : 'Notify me about future events'}
          </button>
          {!member && <p className="text-[12px] text-text-muted mt-2">We will send a quick confirmation email first. You can unsubscribe at any time.</p>}
        </form>
      )}
    </div>
  );
};

const Notice = ({ Icon = Star, children }) => (
  <div className="flex gap-2.5 items-start p-3.5 rounded-arch bg-lavender/20 text-[13.5px] leading-relaxed text-text-main mt-4">
    <Icon className="w-[18px] h-[18px] mt-0.5 shrink-0 text-lavender-dark" aria-hidden="true" />
    <span>{children}</span>
  </div>
);

const Price = ({ event, member }) => {
  const mine = member ? event.your_price_cents : event.price_cents;
  return (
    <div className="flex items-baseline gap-2">
      <span className="font-heading text-4xl font-semibold text-text-main" data-testid="event-price">{formatPrice(mine)}</span>
      {member && event.premium_discount_applied && (
        <s className="text-lg text-text-muted">{formatPrice(event.price_cents)}</s>
      )}
    </div>
  );
};

/** Right-hand panel: price, availability and the one action that makes sense right now. */
const BuyPanel = ({ event, member, profile, onRegister, onSignUp, onSignIn, onNavigate }) => {
  const reg = event.my_registration;
  const registered = reg && ['confirmed', 'attended', 'refund_pending'].includes(reg.status);
  const discountPct = event.premium_discount_pct;
  const memberPrice = formatPrice(event.member_price_cents);

  if (!event.is_ticketed) {
    return (
      <aside className="glass-card rounded-arch-xl p-6 sm:p-7 lg:sticky lg:top-24" aria-label="Event details">
        <p className="font-heading text-xl font-semibold text-text-main">Free for members</p>
        <p className="text-sm text-text-muted mt-1">No ticket needed.</p>
        {member && event.online_url && (
          <a href={event.online_url} target="_blank" rel="noopener noreferrer"
            className="mt-5 min-h-[48px] w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-white font-bold btn-hover">
            <LinkIcon className="w-4 h-4" aria-hidden="true" /> Join online
          </a>
        )}
        {member && event.space_id && (
          <button onClick={() => onNavigate(`space:${event.space_id}`)}
            className="mt-3 min-h-[48px] w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-white font-bold btn-hover">
            <GraduationCap className="w-4 h-4" aria-hidden="true" /> Go to the course
          </button>
        )}
        {member && <AddToCalendar event={event} />}
        {!member && (
          <button onClick={onSignUp} className="mt-5 min-h-[48px] w-full rounded-full bg-primary text-white font-bold btn-hover">
            Create My Free Account
          </button>
        )}
      </aside>
    );
  }

  const deadline = refundDeadline(event);
  return (
    <aside className="glass-card rounded-arch-xl p-6 sm:p-7 lg:sticky lg:top-24" aria-label="Tickets">
      {registered ? (
        <>
          <div className="flex items-center gap-2.5 p-3.5 rounded-arch bg-teal/20 font-bold text-teal-dark dark:text-teal-light" data-testid="event-registered">
            <Check className="w-5 h-5" aria-hidden="true" /> You're registered
          </div>
          <div className="flex gap-2.5 items-start p-3.5 rounded-arch bg-white/60 dark:bg-white/5 mt-3 text-sm leading-relaxed">
            {event.venue_name || event.venue_address
              ? <MapPin className="w-[18px] h-[18px] mt-0.5 shrink-0 text-primary" aria-hidden="true" />
              : <LinkIcon className="w-[18px] h-[18px] mt-0.5 shrink-0 text-primary" aria-hidden="true" />}
            <span data-testid="event-address">
              {event.venue_name && <strong className="block">{event.venue_name}</strong>}
              {event.venue_address}
              {event.online_url && <a href={event.online_url} target="_blank" rel="noopener noreferrer" className="block text-primary underline mt-1">Open the online link</a>}
              {!event.venue_name && !event.venue_address && !event.online_url && 'Details are coming soon.'}
            </span>
          </div>
          <AddToCalendar event={event} />
          <p className="text-[12.5px] text-text-muted text-center mt-4 leading-relaxed" data-testid="event-private-note">
            This event is for ticket holders only. Please do not share the address or details publicly.
          </p>
          <p className="text-[12.5px] text-text-muted text-center mt-2 leading-relaxed">
            Need to cancel? Email <a className="text-primary underline" href="mailto:salam@sirati.co">salam@sirati.co</a> before {formatDateTimeShort(deadline, event.timezone)} for a full refund.
          </p>
        </>
      ) : (
        <>
          <Price event={event} member={member} />
          {member && !profile?.is_premium && event.premium_discount_pct > 0 && event.price_cents > 0 && (
            <Notice><b>Sirati+ members pay {memberPrice}</b> and get early access to tickets.</Notice>
          )}
          {member && profile?.is_premium && event.premium_discount_applied && (
            <Notice><b>Your Sirati+ discount of {discountPct}% is applied.</b></Notice>
          )}
          {!member && event.price_cents > 0 && discountPct > 0 && (
            <Notice><b>Sirati+ members pay {memberPrice}</b> and get early access to tickets.</Notice>
          )}
          <SpotsBar event={event} />
          <div className="flex gap-2.5 items-start p-3.5 rounded-arch bg-white/60 dark:bg-white/5 mt-4 text-sm">
            <Lock className="w-[18px] h-[18px] mt-0.5 shrink-0 text-primary" aria-hidden="true" />
            <span>The venue address is shown after you register.</span>
          </div>

          {!member && event.spots_left === 0 ? (
            <>
              <button disabled className="mt-5 min-h-[48px] w-full rounded-full font-bold bg-teal/50 text-white">Sold out</button>
              <NotifyMe member={false} />
              <p className="text-[12.5px] text-text-muted text-center mt-3.5">
                Already a member? <button onClick={onSignIn} className="text-primary underline">Sign in</button>
              </p>
            </>
          ) : !member ? (
            <>
              <button onClick={onSignUp} className="mt-5 min-h-[48px] w-full rounded-full bg-primary text-white font-bold btn-hover" data-testid="event-signup-btn">
                Create My Free Account to Register
              </button>
              <p className="text-[12.5px] text-text-muted text-center mt-3.5 leading-relaxed">
                Already a member? <button onClick={onSignIn} className="text-primary underline">Sign in</button><br />
                Full refund up to {event.refund_window_hours} hours before the event.
              </p>
            </>
          ) : (
            <MemberAction event={event} reg={reg} onRegister={onRegister} onNavigate={onNavigate} />
          )}
        </>
      )}
    </aside>
  );
};

const MemberAction = ({ event, reg, onRegister, onNavigate }) => {
  const { state, opens_at: opensAt } = event.sale;
  const soldOut = event.spots_left === 0;
  const btn = 'mt-5 min-h-[48px] w-full rounded-full font-bold transition-all';
  if (state === 'ended' || state === 'closed') {
    return <button disabled className={`${btn} bg-teal/50 text-white`}>{state === 'ended' ? 'This event has started' : 'Registration closed'}</button>;
  }
  if (state === 'presale_locked') {
    return (
      <>
        <button disabled className={`${btn} bg-teal/50 text-white`}>
          Opens to everyone {opensAt ? formatDateTimeShort(opensAt, event.timezone) : 'soon'}
        </button>
        <button onClick={() => onNavigate('premium')} className="mt-3 min-h-[48px] w-full rounded-full border-2 border-teal/50 text-primary font-bold hover:bg-teal/10">
          Get Sirati+ for early access
        </button>
      </>
    );
  }
  if (state === 'not_yet') {
    return <button disabled className={`${btn} bg-teal/50 text-white`}>Tickets open {opensAt ? formatDateTimeShort(opensAt, event.timezone) : 'soon'}</button>;
  }
  if (soldOut) {
    return (
      <>
        <button disabled className={`${btn} bg-teal/50 text-white`}>Sold out</button>
        <NotifyMe member />
      </>
    );
  }
  const holding = reg?.status === 'pending_payment';
  return (
    <>
      <button onClick={onRegister} className={`${btn} bg-primary text-white btn-hover`} data-testid="event-register-btn">
        {holding ? 'Finish registering' : `Register for ${formatPrice(event.your_price_cents)}`}
      </button>
      <p className="text-[12.5px] text-text-muted text-center mt-3">Full refund up to {event.refund_window_hours} hours before the event.</p>
    </>
  );
};

const AddToCalendar = ({ event }) => (
  <div className="mt-4 grid gap-2.5" data-testid="add-to-calendar">
    <a
      href={googleCalendarUrl(event)} target="_blank" rel="noopener noreferrer"
      className="min-h-[48px] w-full inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary/50 text-primary font-bold hover:bg-primary/10"
      data-testid="add-to-google-calendar"
    >
      <CalendarDays className="w-4 h-4" aria-hidden="true" /> Add to Google Calendar
    </a>
    <button
      onClick={() => downloadIcs(event)}
      className="min-h-[48px] w-full inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary/50 text-primary font-bold hover:bg-primary/10"
      data-testid="add-to-ics-calendar"
    >
      <Download className="w-4 h-4" aria-hidden="true" /> Apple or Outlook calendar
    </button>
  </div>
);

/**
 * One event. Signed-in members get the full page with checkout; logged-out visitors get the public
 * teaser (no address) with a sign-up button that returns them here after signup and onboarding.
 */
const EventPage = ({ eventId, member, onBack, onNavigate, onSignUp, onSignIn }) => {
  const { profile } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [checkout, setCheckout] = useState(false);
  const [announce, setAnnounce] = useState('');

  const load = useCallback(async () => {
    try {
      const res = member ? await api.getEvent(eventId) : await api.getPublicEvent(eventId);
      setEvent(res.data);
      setMissing(false);
    } catch {
      setMissing(true);
    } finally {
      setLoading(false);
    }
  }, [eventId, member]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  // Refresh behind the popup; the member closes the popup themselves with "Done"
  const handleRegistered = async () => {
    await load();
    setAnnounce("You're registered. The address is now shown on this page.");
  };

  if (loading) {
    return (
      <div className="max-w-[1100px] mx-auto p-4 lg:p-8">
        <div className="animate-pulse space-y-4"><div className="h-[250px] bg-teal/5 rounded-arch-xl" /><div className="h-10 w-2/3 bg-teal/10 rounded" /></div>
      </div>
    );
  }
  if (missing || !event) {
    return (
      <div className="max-w-[1100px] mx-auto p-6 text-center" data-testid="event-missing">
        <p className="text-text-muted mt-10">This event is not available.</p>
        <button onClick={onBack} className="mt-4 text-primary underline min-h-[44px]">{member ? 'Back to the calendar' : 'Go to Sirati'}</button>
      </div>
    );
  }

  const kind = kindOf(event);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-[1100px] mx-auto p-4 lg:p-8 pb-28" data-testid="event-page">
      <div role="status" aria-live="polite" className="sr-only">{announce}</div>
      <button onClick={onBack} className="min-h-[44px] mb-3 inline-flex items-center gap-1 text-primary font-semibold" data-testid="event-back-btn">
        <ChevronLeft className="w-5 h-5" aria-hidden="true" />{member ? 'Calendar' : 'Sirati'}
      </button>
      {event.status === 'draft' && <p className="mb-3 px-4 py-2 rounded-arch bg-lavender/25 text-sm font-semibold">Draft: only admins can see this event.</p>}
      {event.status === 'cancelled' && <p className="mb-3 px-4 py-2 rounded-arch bg-red-100 text-red-700 text-sm font-semibold">This event was cancelled.</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-5 items-start">
        <div>
          <Hero event={event} />
          <div className="glass-card rounded-arch-xl p-6 sm:p-8 mt-5">
            <h1 className="font-heading text-[27px] sm:text-4xl font-semibold leading-tight text-text-main">{event.title}</h1>
            {event.venue_partner && <p className="text-[15px] text-text-muted mt-2">Hosted with {event.venue_partner}</p>}
            <div className="grid gap-3 my-6">
              <MetaRow Icon={CalendarDays} label="Date" value={formatEventDate(event)} />
              <MetaRow Icon={Clock} label="Time" value={formatEventTime(event)} />
              {event.event_type === 'online'
                ? <MetaRow Icon={kind.Icon} label="Where" value="Online" />
                : event.city && <MetaRow Icon={kind.Icon} label="Location" value={event.city} />}
            </div>
            {event.description && (
              <section>
                <h2 className="font-heading text-xl font-semibold text-text-main mb-2">About this event</h2>
                <p className="text-[15px] leading-relaxed text-text-main whitespace-pre-line"><BoldText text={event.description} /></p>
              </section>
            )}
            {event.is_ticketed && (
              <section>
                <h2 className="font-heading text-xl font-semibold text-text-main mt-6 mb-2">Good to know</h2>
                <ul className="list-disc pl-5 text-[15px] leading-relaxed text-text-main space-y-1">
                  <li>18 and over only.</li>
                  <li>Our community code of conduct applies for the whole event.</li>
                  {event.event_type === 'in_person' && <li>The exact address is shared with you once you register.</li>}
                </ul>
              </section>
            )}
          </div>
        </div>
        <BuyPanel
          event={event} member={member} profile={profile}
          onRegister={() => setCheckout(true)} onSignUp={onSignUp} onSignIn={onSignIn} onNavigate={onNavigate}
        />
      </div>

      {checkout && <EventCheckout event={event} onClose={() => setCheckout(false)} onSuccess={handleRegistered} />}
    </motion.div>
  );
};

export default EventPage;
